import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Copy, FileDown, ShieldAlert, Languages } from "lucide-react";
import { useEntitlement } from "../lib/entitlement";
import { isProActive } from "../lib/featureAccess";
import UpgradeButton from "./UpgradeButton";
import SubscribeModal from "./SubscribeModal";
import PolishedReportView from "./PolishedReportView";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Source-language options for Translate & Polish. "auto-detect" is default
// and tells the backend system prompt to detect from the input.
export const TRANSLATE_SOURCE_LANGUAGES = [
  { value: "auto-detect", label: "Auto-detect" },
  { value: "English", label: "English" },
  { value: "Hindi", label: "Hindi" },
  { value: "Arabic", label: "Arabic" },
  { value: "Spanish", label: "Spanish" },
  { value: "French", label: "French" },
  { value: "Portuguese", label: "Portuguese" },
  { value: "Urdu", label: "Urdu" },
  { value: "Tagalog", label: "Tagalog" },
  { value: "Bahasa Indonesia", label: "Bahasa Indonesia" },
  { value: "Other", label: "Other" },
];

/**
 * PolishPanel — shared Polish Report + Translate & Polish UI.
 *
 * Two independent actions:
 *   1. "Polish Report" → polishes the deterministic draft + rough notes into
 *      a professional draft in the source language.
 *   2. "Translate & Polish" → translates non-English content from
 *      structured fields + rough notes into professional English, then
 *      polishes. The result renders in a SEPARATE card titled
 *      "Professionally Polished English Draft".
 *
 * Contract guarantees:
 *   - Rough notes are NEVER mutated by this panel.
 *   - Deterministic draft is NEVER mutated.
 *   - Each output lives in its own React state slot + distinct testid.
 *   - The OpenAI / Emergent LLM key never reaches the browser.
 */
export default function PolishPanel({
  testIdPrefix,
  reportType,
  reportTitle,
  safetyCritical = false,
  structuredData,
  roughNotes,
  deterministicDraft,
  onExportPdf,
  onExportTranslatedPdf,
}) {
  const [polishLoading, setPolishLoading] = useState(false);
  const [polishedDraft, setPolishedDraft] = useState("");
  const [polishWarnings, setPolishWarnings] = useState([]);
  const [polishError, setPolishError] = useState("");
  const [subscribeModalOpen, setSubscribeModalOpen] = useState(false);
  const [polishModel, setPolishModel] = useState("");

  const [tpLoading, setTpLoading] = useState(false);
  const [tpDraft, setTpDraft] = useState("");
  const [tpWarnings, setTpWarnings] = useState([]);
  const [tpError, setTpError] = useState("");
  const [tpModel, setTpModel] = useState("");
  const [sourceLang, setSourceLang] = useState("auto-detect");

  const [providerDisplayName, setProviderDisplayName] = useState("");
  const ent = useEntitlement();

  useEffect(() => {
    if (!API_BASE) return;
    let cancelled = false;
    fetch(`${API_BASE}/api/reports/polish/status`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!cancelled && j && typeof j.providerDisplayName === "string") {
          setProviderDisplayName(j.providerDisplayName);
        }
      })
      .catch(() => { /* silent fallback to generic notice */ });
    return () => { cancelled = true; };
  }, []);

  const _postPolish = async (extra = {}) => {
    const res = await fetch(`${API_BASE}/api/reports/polish`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        reportType,
        reportTitle,
        structuredData,
        roughNotes: roughNotes || "",
        deterministicDraft,
        safetyCategory: safetyCritical ? "safety-critical" : "standard",
        sourceRoute: typeof window !== "undefined" ? window.location.pathname : undefined,
        ...extra,
      }),
    });
    if (!res.ok) {
      let detail = "Report polishing is temporarily unavailable. Please use the deterministic draft for now.";
      try {
        const j = await res.json();
        if (j && j.detail) detail = String(j.detail);
      } catch (_) { /* keep default */ }
      throw new Error(detail);
    }
    return res.json();
  };

  const onPolish = async () => {
    setPolishLoading(true);
    setPolishError("");
    try {
      const data = await _postPolish();
      setPolishedDraft(String(data.polishedDraft || ""));
      setPolishWarnings(Array.isArray(data.warnings) ? data.warnings : []);
      setPolishModel(data.modelUsed || "");
      toast.success("Polished draft ready");
    } catch (e) {
      const msg = String(e.message || "Report polishing is temporarily unavailable. Please use the deterministic draft for now.");
      setPolishError(msg);
      toast.error(msg);
    } finally {
      setPolishLoading(false);
    }
  };

  const onTranslateAndPolish = async () => {
    setTpLoading(true);
    setTpError("");
    try {
      const data = await _postPolish({
        translateAndPolish: true,
        sourceLanguage: sourceLang,
        targetLanguage: "English",
      });
      setTpDraft(String(data.polishedDraft || ""));
      setTpWarnings(Array.isArray(data.warnings) ? data.warnings : []);
      setTpModel(data.modelUsed || "");
      toast.success("Translated & polished draft ready");
    } catch (e) {
      const msg = String(e.message || "Translate & polish is temporarily unavailable.");
      setTpError(msg);
      toast.error(msg);
    } finally {
      setTpLoading(false);
    }
  };

  const onCopyPolished = async () => {
    try { await navigator.clipboard.writeText(polishedDraft); toast.success("Polished draft copied"); }
    catch { toast.error("Copy failed"); }
  };
  const onCopyTranslated = async () => {
    try { await navigator.clipboard.writeText(tpDraft); toast.success("Translated draft copied"); }
    catch { toast.error("Copy failed"); }
  };
  const onPdfPolished = () => {
    if (typeof onExportPdf === "function") {
      try { onExportPdf(polishedDraft); toast.success("Polished PDF exported"); }
      catch { toast.error("Polished PDF export failed"); }
    }
  };
  const onPdfTranslated = () => {
    const handler = typeof onExportTranslatedPdf === "function" ? onExportTranslatedPdf : onExportPdf;
    if (typeof handler === "function") {
      try { handler(tpDraft); toast.success("Translated PDF exported"); }
      catch { toast.error("Translated PDF export failed"); }
    }
  };

  if (!reportType || !API_BASE) return null;

  const providerLabel = providerDisplayName || "the configured secure language-processing provider";
  // RigSentinel Pro hard gate: Polish + Translate & Polish are unlocked only
  // for users with an active paid subscription. While the entitlement is
  // still loading we keep the panel locked (fail closed). The
  // RSC_ENFORCE_PROFESSIONAL marketing flag is intentionally NOT consulted
  // here — the gate is server-enforced regardless of the flag.
  const polishUnlocked = isProActive(ent);

  return (
    <>
      <div className="flex flex-wrap items-center gap-2" data-testid={`${testIdPrefix}-polish-actions`}>
        {polishUnlocked ? (
          <>
            <button
              data-testid={`${testIdPrefix}-polish-btn`}
              onClick={onPolish}
              disabled={polishLoading}
              className="btn-ghost h-9 px-3 rounded-md text-xs label-up inline-flex items-center gap-1.5 disabled:opacity-50"
              style={{ borderColor: "rgba(255,179,0,0.55)", color: "var(--rs-amber)" }}
              title="Generate a professionally polished draft from your rough notes and entered data."
            >
              <Sparkles className="w-3.5 h-3.5" /> {polishLoading ? "Polishing report..." : "Polish Report"}
            </button>
            <button
              data-testid={`${testIdPrefix}-translate-polish-btn`}
              onClick={onTranslateAndPolish}
              disabled={tpLoading}
              className="btn-ghost h-9 px-3 rounded-md text-xs label-up inline-flex items-center gap-1.5 disabled:opacity-50"
              style={{ borderColor: "rgba(56,189,248,0.55)", color: "rgb(125,211,252)" }}
              title="Translate non-English notes & data into professional English, then polish."
            >
              <Languages className="w-3.5 h-3.5" /> {tpLoading ? "Translating & polishing..." : "Translate & Polish"}
            </button>
            <label className="flex items-center gap-1.5 text-[10.5px] label-up text-[var(--rs-text-3)]">
              Source language
              <select
                data-testid={`${testIdPrefix}-polish-source-lang`}
                value={sourceLang}
                onChange={(e) => setSourceLang(e.target.value)}
                className="h-8 px-2 rounded-md text-[11px] bg-transparent border border-[var(--rs-border)] text-[var(--rs-text-2)]"
                style={{ background: "rgba(22,24,29,0.65)" }}
              >
                {TRANSLATE_SOURCE_LANGUAGES.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
          </>
        ) : (
          <>
            <button
              type="button"
              data-testid={`${testIdPrefix}-polish-btn-locked`}
              onClick={() => setSubscribeModalOpen(true)}
              className="btn-ghost h-9 px-3 rounded-md text-xs label-up inline-flex items-center gap-1.5"
              style={{ borderColor: "rgba(255,179,0,0.55)", color: "var(--rs-amber)" }}
              title="Subscribe to RigSentinel Pro to unlock Polish."
            >
              <Sparkles className="w-3.5 h-3.5" /> Polish Report
            </button>
            <button
              type="button"
              data-testid={`${testIdPrefix}-translate-polish-btn-locked`}
              onClick={() => setSubscribeModalOpen(true)}
              className="btn-ghost h-9 px-3 rounded-md text-xs label-up inline-flex items-center gap-1.5"
              style={{ borderColor: "rgba(56,189,248,0.55)", color: "rgb(125,211,252)" }}
              title="Subscribe to RigSentinel Pro to unlock Translate & Polish."
            >
              <Languages className="w-3.5 h-3.5" /> Translate & Polish
            </button>
          </>
        )}
      </div>
      <SubscribeModal open={subscribeModalOpen} onClose={() => setSubscribeModalOpen(false)} />

      {(polishedDraft || polishError || polishLoading) && (
        <section
          className="card-rs p-3.5 mt-3"
          data-testid={`${testIdPrefix}-polished-card`}
          style={{ borderColor: polishError ? "rgba(239,68,68,0.6)" : "rgba(255,179,0,0.55)" }}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="label-up text-[var(--rs-amber)] inline-flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Professionally Polished Draft
            </div>
            {polishedDraft && !polishError && (
              <div className="flex items-center gap-2">
                <button data-testid={`${testIdPrefix}-polish-copy-btn`} onClick={onCopyPolished} className="btn-ghost h-7 px-2 rounded-md text-[10.5px] label-up inline-flex items-center gap-1.5">
                  <Copy className="w-3 h-3" /> Copy
                </button>
                {typeof onExportPdf === "function" && (
                  <button data-testid={`${testIdPrefix}-polish-pdf-btn`} onClick={onPdfPolished} className="btn-ghost h-7 px-2 rounded-md text-[10.5px] label-up inline-flex items-center gap-1.5">
                    <FileDown className="w-3 h-3" /> Export PDF
                  </button>
                )}
              </div>
            )}
          </div>
          {polishLoading && (<div data-testid={`${testIdPrefix}-polish-loading`} className="text-[12px] text-[var(--rs-text-2)] py-3 px-2">Polishing report...</div>)}
          {polishError && !polishLoading && (<div data-testid={`${testIdPrefix}-polish-error`} className="text-[12px] py-2 px-2" style={{ color: "rgb(252,165,165)" }}>{polishError}</div>)}
          {polishedDraft && !polishLoading && !polishError && (
            <>
              <PolishedReportView
                text={polishedDraft}
                testid={`${testIdPrefix}-polished-text`}
              />
              {polishModel && (<div className="mt-2 text-[10px] text-[var(--rs-text-3)]" data-testid={`${testIdPrefix}-polish-model`}>Model: {polishModel} · Generated {new Date().toLocaleString()}</div>)}
              {polishWarnings.length > 0 && (
                <ul className="mt-2 space-y-1" data-testid={`${testIdPrefix}-polish-warnings`}>
                  {polishWarnings.map((w, i) => (
                    <li key={i} className="text-[10.5px] text-[var(--rs-text-3)] leading-snug flex items-start gap-1.5">
                      <ShieldAlert className="w-3 h-3 mt-[2px] shrink-0" style={{ color: "var(--rs-amber)" }} /><span>{w}</span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
          <p className="mt-2 text-[10px] text-[var(--rs-text-3)] leading-snug border-t border-[var(--rs-border)] pt-2" data-testid={`${testIdPrefix}-polish-privacy-notice`}>
            Report polishing may process the entered draft through {providerLabel}. Verify company data-sharing rules before using this feature.
          </p>
        </section>
      )}

      {(tpDraft || tpError || tpLoading) && (
        <section
          className="card-rs p-3.5 mt-3"
          data-testid={`${testIdPrefix}-translated-card`}
          style={{ borderColor: tpError ? "rgba(239,68,68,0.6)" : "rgba(56,189,248,0.55)" }}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="label-up inline-flex items-center gap-1.5" style={{ color: "rgb(125,211,252)" }}>
              <Languages className="w-3.5 h-3.5" /> Professionally Polished English Draft
            </div>
            {tpDraft && !tpError && (
              <div className="flex items-center gap-2">
                <button data-testid={`${testIdPrefix}-translated-copy-btn`} onClick={onCopyTranslated} className="btn-ghost h-7 px-2 rounded-md text-[10.5px] label-up inline-flex items-center gap-1.5">
                  <Copy className="w-3 h-3" /> Copy
                </button>
                {(typeof onExportTranslatedPdf === "function" || typeof onExportPdf === "function") && (
                  <button data-testid={`${testIdPrefix}-translated-pdf-btn`} onClick={onPdfTranslated} className="btn-ghost h-7 px-2 rounded-md text-[10.5px] label-up inline-flex items-center gap-1.5">
                    <FileDown className="w-3 h-3" /> Export PDF
                  </button>
                )}
              </div>
            )}
          </div>
          {tpLoading && (<div data-testid={`${testIdPrefix}-translated-loading`} className="text-[12px] text-[var(--rs-text-2)] py-3 px-2">Translating &amp; polishing...</div>)}
          {tpError && !tpLoading && (<div data-testid={`${testIdPrefix}-translated-error`} className="text-[12px] py-2 px-2" style={{ color: "rgb(252,165,165)" }}>{tpError}</div>)}
          {tpDraft && !tpLoading && !tpError && (
            <>
              <PolishedReportView
                text={tpDraft}
                testid={`${testIdPrefix}-translated-text`}
              />
              {tpModel && (<div className="mt-2 text-[10px] text-[var(--rs-text-3)]" data-testid={`${testIdPrefix}-translated-model`}>Model: {tpModel} · Source language: {sourceLang} · Target: English · Generated {new Date().toLocaleString()}</div>)}
              {tpWarnings.length > 0 && (
                <ul className="mt-2 space-y-1" data-testid={`${testIdPrefix}-translated-warnings`}>
                  {tpWarnings.map((w, i) => (
                    <li key={i} className="text-[10.5px] text-[var(--rs-text-3)] leading-snug flex items-start gap-1.5">
                      <ShieldAlert className="w-3 h-3 mt-[2px] shrink-0" style={{ color: "rgb(125,211,252)" }} /><span>{w}</span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
          <p className="mt-2 text-[10px] text-[var(--rs-text-3)] leading-snug border-t border-[var(--rs-border)] pt-2" data-testid={`${testIdPrefix}-translated-privacy-notice`}>
            Translate &amp; Polish may process the entered draft through {providerLabel}. Verify company data-sharing rules before using this feature.
          </p>
        </section>
      )}
    </>
  );
}
